import React, { useState, useRef, useCallback, useEffect } from 'react';

import i18n from 'i18n-js';
import { useSelector, useDispatch } from 'react-redux';
import { Table } from 'components/UI';
import { FETCH_STATE } from 'constants';
import { debounce } from 'lodash';
import { ReservationsActions } from 'state/modules/reservations';
import { DatetimeField } from 'components/DatetimeField';
import { useHistory } from 'react-router-dom';
import { buildUrl } from 'utils';

export const ReservationDetails = ({ invoiceItemId }) => {
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    invoice_item_id: invoiceItemId,
  });

  const isMounted = useRef(false);
  const abortController = useRef(null);
  const history = useHistory();

  const loadingReservations = useSelector((state) => state.reservations?.loading);
  const reservations = useSelector((state) => state.reservations?.reservations);
  const pagination = useSelector((state) => state.reservations?.pagination);

  const dispatch = useDispatch();

  const loading = loadingReservations === FETCH_STATE.LOADING;
  const orgType = 'BUSINESS';
  const hasPartnership = 'FALSE';
  const classes = {};
  const isOwner = orgType === 'msOwnerOrg';

  const columns = [
    {
      name: i18n.t('tableColumns.id'),
      selector: 'id',
      sortable: true,
      grow: 1,
      maxWidth: '90px',
      minWidth: '70px',
    },
    {
      name: isOwner ? i18n.t('common.employee') : i18n.t('tableColumns.customer'),
      selector: (row) =>
        row?.user?.first_name || row?.user?.last_name
          ? (row?.user?.first_name || '') + ' ' + (row?.user?.last_name || '')
          : '--',
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.start'),
      selector: 'used_times',
      sortable: true,
      minWidth: '165px',
      cell: (row) =>
        row?.start_reserved ? (
          <div>
            <DatetimeField datetime={row?.start_reserved} tz={row?.vehicle_group?.time_zone} />
            {row?.start_used && (
              <div className={classes?.time}>
                <DatetimeField datetime={row?.start_used} tz={row?.vehicle_group?.time_zone} />
              </div>
            )}
          </div>
        ) : (
          '--'
        ),
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.end'),
      minWidth: '165px',
      cell: (row) =>
        row?.end_reserved ? (
          <div>
            <DatetimeField datetime={row?.end_reserved} tz={row?.vehicle_group?.time_zone} />
            {row?.end_used && (
              <div className={classes?.time}>
                <DatetimeField datetime={row?.end_used} tz={row?.vehicle_group?.time_zone} />
              </div>
            )}
          </div>
        ) : (
          '--'
        ),
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.vehicle_group'),
      selector: (row) => row?.vehicle_group?.name || '--',
      grow: 2,
      wrap: true,
      omit: hasPartnership,
    },
    {
      name: i18n.t('tableColumns.vehicle'),
      selector: (row) => row?.vehicle?.name || '--',
      grow: 1,
      wrap: true,
    },
    {
      name: i18n.t('tableColumns.location'),
      selector: (row) => row?.start_reserved_location?.name || '--',
      grow: 1,
      wrap: true,
      minWidth: '120px',
    },
    {
      name: i18n.t('common.fleet'),
      selector: (row) => row?.vehicle_group?.parent?.name || '--',
      grow: 2,
      wrap: true,
    },
    {
      name: i18n.t('common.fleet_owner'),
      selector: (row) => row?.vehicle_group?.vehicle_owner?.name || '--',
      grow: 2,
      wrap: true,
      omit: orgType !== 'msServiceOrg',
    },
    {
      name: i18n.t('tableColumns.service'),
      selector: (row) => row?.organization?.name || '--',
      grow: 1,
      minWidth: '110px',
      wrap: true,
      omit: orgType !== 'msOwnerOrg',
    },
    {
      name: i18n.t('tableColumns.status'),
      grow: 1,
      minWidth: '120px',
      selector: (row) =>
        row?.status ? (
          <div className={`${classes.status} ${classes[row?.status.toLowerCase()]}`}>
            <span className="status">
              {i18n.t(`reservations_statuses.${row?.status}`, {
                defaultValue: row?.status,
              })}
            </span>
          </div>
        ) : (
          '--'
        ),
    },
  ];

  const handleParams = (newParams) => {
    setParams({ ...newParams });
  };

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      page: pagination?.currentPage,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  const handleRowClick = (row) => {
    history.push(buildUrl(`/reservations/${row?.id}`));
  };

  const handlePerRowsChange = useCallback((limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  }, []);

  const handlePageChange = useCallback((page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  }, []);

  const fetchReservations = useCallback(
    (params) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        ReservationsActions.fetchReservations(params, { signal: abortController?.current?.signal })
      );
    },
    [dispatch]
  );

  const fetchReservationsDebounced = useCallback(
    debounce((params) => {
      fetchReservations(params);
    }, 1000),
    [fetchReservations]
  );

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchReservationsDebounced(params);
    }
  }, [fetchReservationsDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchReservations(params);
    isMounted.current = true;
  }, []);

  return (
    <Table
      data={reservations}
      columns={columns}
      page={params.page}
      onSort={handleSort}
      limit={params.limit}
      loading={loading}
      handleRowClicked={!isOwner ? handleRowClick : null}
      handlePageChange={handlePageChange}
      defaultSortAsc={!params?.sort_desc}
      defaultSortField={params?.sort_field}
      totalRows={pagination?.totalRows || 0}
      handlePerRowsChange={handlePerRowsChange}
      paginationDefaultPage={pagination?.currentPage}
    />
  );
};
